export interface PackingItem {
  id: string;
  lengthCm: number;
  widthCm: number;
  heightCm: number;
  weightKg: number;
  quantity: number;
  color: string;
  /**
   * Frágil: encima sólo va más del mismo producto (Comercia › Carga, 28-oct).
   * Las frágiles se acomodan al final, arriba de todo. Opcional: sin el dato,
   * nada cambia.
   */
  fragile?: boolean;
}

export interface Placement {
  itemId: string;
  x: number;
  y: number;
  z: number;
  rotated: boolean;
  lengthCm: number;
  widthCm: number;
  heightCm: number;
}

export interface PackingResult {
  placements: Placement[];
  usedVolumeCbm: number;
  usedWeightKg: number;
  fillPercent: number;
  unplacedItems: string[];
}

interface Dimensions {
  lengthCm: number;
  widthCm: number;
  heightCm: number;
}

interface ExtremePoint {
  x: number;
  y: number;
  z: number;
}

function doesOverlap(
  a: Placement,
  bx: number,
  by: number,
  bz: number,
  bl: number,
  bw: number,
  bh: number
): boolean {
  return !(
    bx >= a.x + a.lengthCm ||
    bx + bl <= a.x ||
    by >= a.y + a.widthCm ||
    by + bw <= a.y ||
    bz >= a.z + a.heightCm ||
    bz + bh <= a.z
  );
}

function fitsInContainer(
  x: number,
  y: number,
  z: number,
  l: number,
  w: number,
  h: number,
  container: Dimensions
): boolean {
  return x + l <= container.lengthCm && y + w <= container.widthCm && z + h <= container.heightCm;
}

/** Cuánta de la base de una caja tiene que apoyar para considerarla estable. */
const APOYO_MINIMO = 0.7;

/**
 * Hasta qué alto se apila lo frágil sobre sí mismo, en cm.
 *
 * Sin tope, auto-acomodar levantaba una torre de notebooks de piso a techo
 * (2,4 m de cajas de 8 cm): la de abajo no aguanta ese peso y una torre tan
 * angosta se cae en el viaje.
 */
const ALTO_MAXIMO_FRAGIL = 120;

/**
 * Y además, una pila de lo frágil no es más alta que una vez y media su lado
 * más corto: así queda baja y ancha, firme aunque no tenga nada al costado.
 *
 * 🔴 Con sólo el tope de 1,20 m, las notebooks que no entraban arriba de la
 * carga quedaban en una torre de 15 cajas (40 × 30 cm de base), sola junto a la
 * puerta (ensayo de Comercia del 30-oct). Ahora van de a 5, en pilas de 40 cm.
 */
const ESBELTEZ_MAXIMA_FRAGIL = 1.5;

const altoMaximoDeLaPila = (l: number, w: number) =>
  Math.min(ALTO_MAXIMO_FRAGIL, ESBELTEZ_MAXIMA_FRAGIL * Math.min(l, w));

/** Las frágiles ya puestas: de qué producto son y dónde arranca su pila. */
type Fragiles = ReadonlyMap<Placement, { producto: string; base: number }>;

/**
 * ¿Hay algo debajo que la sostenga?
 *
 * 🔴 No se comprobaba. Los puntos candidatos incluyen las esquinas laterales de
 * cada caja ya puesta, y esas esquinas heredan su altura: una caja podía
 * quedar a 1,80 m con aire debajo. En la escena se veían bultos flotando, que
 * es parte de lo que parecía "se sale del contenedor".
 *
 * Se exige que el 70 % de la base apoye sobre el piso o sobre otras cajas. No
 * el 100 %: en una estiba real un bulto puede volar un poco sobre el borde del
 * de abajo, y exigir apoyo total dejaría huecos que nadie deja.
 */
function tieneApoyo(
  x: number,
  y: number,
  z: number,
  l: number,
  w: number,
  placements: Placement[],
  fragiles?: Fragiles,
  producto?: string
): boolean {
  if (z === 0) return true;

  const areaBase = l * w;
  if (areaBase <= 0) return false;

  let apoyada = 0;
  for (const p of placements) {
    // Solo las que terminan justo a la altura donde arranca esta.
    if (Math.abs(p.z + p.heightCm - z) > 0.01) continue;

    const solapeX = Math.min(x + l, p.x + p.lengthCm) - Math.max(x, p.x);
    const solapeY = Math.min(y + w, p.y + p.widthCm) - Math.max(y, p.y);
    /*
      Encima de algo frágil sólo va el mismo producto: son cajas iguales y del
      mismo peso, que se estiban en columna como vienen de fábrica.

      🔴 Antes no iba nada, ni otra notebook: las 50 quedaban de a una sobre el
      piso y sobre los monitores, en una capa finita (auditoría del 29-oct).
    */
    const fragilDe = fragiles?.get(p)?.producto;
    if (solapeX > 0 && solapeY > 0 && fragilDe !== undefined && fragilDe !== producto) return false;
    if (solapeX > 0 && solapeY > 0) apoyada += solapeX * solapeY;
  }

  return apoyada / areaBase >= APOYO_MINIMO;
}

/**
 * Dónde arranca la pila de lo frágil sobre la que quedaría una caja: la base
 * más baja de las frágiles que tiene justo debajo. Sin frágiles debajo, `z`.
 */
function baseDeLaPilaFragil(
  x: number,
  y: number,
  z: number,
  l: number,
  w: number,
  placements: Placement[],
  fragiles: Fragiles
): number {
  let base = z;
  for (const p of placements) {
    const f = fragiles.get(p);
    if (!f || Math.abs(p.z + p.heightCm - z) > 0.01) continue;
    const solapeX = Math.min(x + l, p.x + p.lengthCm) - Math.max(x, p.x);
    const solapeY = Math.min(y + w, p.y + p.widthCm) - Math.max(y, p.y);
    if (solapeX > 0 && solapeY > 0) base = Math.min(base, f.base);
  }
  return base;
}

export function packContainer(
  container: Dimensions & { maxWeightKg: number; maxVolumeCbm: number },
  items: PackingItem[]
): PackingResult {
  // Expand items by quantity
  const expandedItems: {
    id: string;
    lengthCm: number;
    widthCm: number;
    heightCm: number;
    weightKg: number;
    color: string;
    originalId: string;
    fragile: boolean;
  }[] = [];

  for (const item of items) {
    for (let i = 0; i < item.quantity; i++) {
      expandedItems.push({
        id: `${item.id}_${i}`,
        originalId: item.id,
        lengthCm: item.lengthCm,
        widthCm: item.widthCm,
        heightCm: item.heightCm,
        weightKg: item.weightKg / item.quantity,
        color: item.color,
        fragile: item.fragile === true,
      });
    }
  }

  // Sort by volume descending (First Fit Decreasing). Las frágiles, al final:
  // van arriba de todo, que es donde nada las aplasta.
  expandedItems.sort(
    (a, b) =>
      Number(a.fragile) - Number(b.fragile) ||
      b.lengthCm * b.widthCm * b.heightCm - a.lengthCm * a.widthCm * a.heightCm
  );
  const fragiles = new Map<Placement, { producto: string; base: number }>();

  const placements: Placement[] = [];
  const unplacedItems: string[] = [];
  let totalWeight = 0;
  let totalVolume = 0;

  // Extreme points - start with origin
  let extremePoints: ExtremePoint[] = [{ x: 0, y: 0, z: 0 }];

  for (const item of expandedItems) {
    let placed = false;

    // Try both orientations (swap length and width)
    const orientations = [
      {
        l: item.lengthCm,
        w: item.widthCm,
        h: item.heightCm,
        rotated: false,
      },
      {
        l: item.widthCm,
        w: item.lengthCm,
        h: item.heightCm,
        rotated: true,
      },
    ];

    /*
      Se apila HACIA ARRIBA antes de correrse al costado.

      🔴 El orden era `z, y, x`: primero el punto más bajo. Eso llenaba todo el
      piso desparramando la carga a lo largo del contenedor antes de poner nada
      encima — se veía una sola capa de cajas sueltas ocupando 12 metros.

      Con `x, y, z` se completa la columna del fondo-izquierda hasta el techo y
      recién ahí se avanza. Es además como se estiba de verdad: se apila contra
      la pared y se avanza hacia la puerta.
    */
    extremePoints.sort((a, b) => a.x - b.x || a.y - b.y || a.z - b.z);

    for (const ep of extremePoints) {
      for (const orient of orientations) {
        // Check weight limit
        if (totalWeight + item.weightKg > container.maxWeightKg) continue;

        // Check if fits in container bounds
        if (!fitsInContainer(ep.x, ep.y, ep.z, orient.l, orient.w, orient.h, container)) continue;

        // Check overlap with existing placements
        const hasOverlap = placements.some((p) =>
          doesOverlap(p, ep.x, ep.y, ep.z, orient.l, orient.w, orient.h)
        );
        if (hasOverlap) continue;

        // Nada flotando: si no apoya, se prueba el siguiente punto.
        if (
          !tieneApoyo(ep.x, ep.y, ep.z, orient.l, orient.w, placements, fragiles, item.originalId)
        )
          continue;

        // Una pila de lo frágil no pasa de `altoMaximoDeLaPila` (una sola caja más alta, sí entra).
        const base = item.fragile
          ? baseDeLaPilaFragil(ep.x, ep.y, ep.z, orient.l, orient.w, placements, fragiles)
          : ep.z;
        if (base < ep.z && ep.z + orient.h - base > altoMaximoDeLaPila(orient.l, orient.w))
          continue;

        // Place the item
        const placement: Placement = {
          itemId: item.id,
          x: ep.x,
          y: ep.y,
          z: ep.z,
          rotated: orient.rotated,
          lengthCm: orient.l,
          widthCm: orient.w,
          heightCm: orient.h,
        };
        placements.push(placement);
        if (item.fragile) fragiles.set(placement, { producto: item.originalId, base });

        totalWeight += item.weightKg;
        totalVolume += (orient.l * orient.w * orient.h) / 1000000; // cm3 to m3 (CBM)

        // Generate new extreme points
        extremePoints.push(
          { x: ep.x + orient.l, y: ep.y, z: ep.z },
          { x: ep.x, y: ep.y + orient.w, z: ep.z },
          { x: ep.x, y: ep.y, z: ep.z + orient.h }
        );

        // Remove used extreme point
        extremePoints = extremePoints.filter((p) => p !== ep);

        placed = true;
        break;
      }
      if (placed) break;
    }

    if (!placed) {
      unplacedItems.push(item.originalId);
    }
  }

  const fillPercent = container.maxVolumeCbm > 0 ? (totalVolume / container.maxVolumeCbm) * 100 : 0;

  return {
    placements,
    usedVolumeCbm: Math.round(totalVolume * 100) / 100,
    usedWeightKg: Math.round(totalWeight * 100) / 100,
    fillPercent: Math.round(fillPercent * 10) / 10,
    unplacedItems: [...new Set(unplacedItems)],
  };
}
